import { Breadcrumb as AntdBreadcrumb } from 'antd';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import { resolveSystemRoleLabel } from '~/app/landing/admin/roles/utils';
import {
  isAssetDetailPath,
  isWorkOrderTicketDetailPath,
  resolveBreadcrumbHref,
  toBreadcrumbTitle,
} from '~/app/landing/breadcrumb-utils';
import { assetSummaryQueryKey } from '~/app/hooks/asset/useAssetSummary';
import { AssetService } from '~/app/services/asset/asset.service';
import { RoleService } from '~/app/services/authorization/role.service';
import { MaintenanceExecutionService } from '~/app/services/maintenance-execution/maintenance-execution.service';
import { SiteService } from '~/app/services/site/site.service';
import { SessionService } from '~/app/services/session/session.service';

export function BreadcrumbTrail() {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTranslation('authorization');
  const segments = pathname.split('/').filter(Boolean);
  const isAssetDetailPage = isAssetDetailPath(segments);
  const assetId = isAssetDetailPage ? segments[1] : null;
  const isSiteDetailPage = segments.length === 2 && segments[0] === 'sites';
  const siteId = isSiteDetailPage ? segments[1] : null;
  const isZoneDetailPage =
    segments.length === 4 && segments[0] === 'sites' && segments[2] === 'zones';
  const zoneSiteId = isZoneDetailPage ? segments[1] : null;
  const zoneId = isZoneDetailPage ? segments[3] : null;
  const isRoleDetailPage =
    segments.length === 3 &&
    segments[0] === 'admin' &&
    segments[1] === 'roles' &&
    segments[2] !== 'create';
  const roleId = isRoleDetailPage ? segments[2] : null;
  const isTicketDetailPage = isWorkOrderTicketDetailPath(segments);
  const ticketId = isTicketDetailPage ? segments[3] : null;
  const isChargeDetailPage =
    segments.length === 2 && segments[0] === 'sessions';
  const chargeId = isChargeDetailPage ? segments[1] : null;

  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const organizationIdFromQuery = searchParams.get('organizationId');

  const organizationId = useMemo(() => {
    if (isSuperAdmin) {
      return organizationIdFromQuery ?? user?.organizationId ?? null;
    }
    return user?.organizationId ?? null;
  }, [isSuperAdmin, organizationIdFromQuery, user?.organizationId]);

  const useSystemRoleApi = isSuperAdmin && !organizationIdFromQuery;

  const assetSummaryQuery = useQuery({
    queryKey: assetSummaryQueryKey(assetId ?? ''),
    queryFn: () => {
      if (!assetId || !organizationId) return null;
      return AssetService.findSummaryById(organizationId, assetId);
    },
    enabled: !!assetId && !!organizationId,
  });

  const siteDetailQuery = useQuery({
    queryKey: ['sites', 'assets-map', organizationId, siteId],
    queryFn: () => {
      if (!siteId || !organizationId) return null;
      return SiteService.findAssetsMapById(organizationId, siteId);
    },
    enabled: !!siteId && !!organizationId,
  });

  const zoneDetailQuery = useQuery({
    queryKey: ['zones', 'assets-map', organizationId, zoneSiteId, zoneId],
    queryFn: () => {
      if (!zoneSiteId || !zoneId || !organizationId) return null;
      return SiteService.findZoneAssetsMapById(
        organizationId,
        zoneSiteId,
        zoneId,
      );
    },
    enabled: !!zoneSiteId && !!zoneId && !!organizationId,
  });

  const roleDetailQuery = useQuery({
    queryKey: [
      'role-detail',
      useSystemRoleApi ? 'system' : organizationId,
      roleId,
    ],
    queryFn: () =>
      useSystemRoleApi
        ? RoleService.findSystemRoleById(roleId!)
        : RoleService.findRoleById(organizationId!, roleId!),
    enabled: Boolean(roleId && (useSystemRoleApi || organizationId)),
  });

  const ticketDetailQuery = useQuery({
    queryKey: [
      'maintenance-execution',
      'ticket',
      'breadcrumb',
      ticketId,
      organizationId,
    ],
    queryFn: () =>
      MaintenanceExecutionService.getTicket(
        ticketId!,
        organizationId ?? undefined,
      ),
    enabled: Boolean(ticketId && organizationId),
  });

  const chargeDetailQuery = useQuery({
    queryKey: ['session', 'charge', user?.organizationId, chargeId],
    queryFn: () => SessionService.getCharge(chargeId!),
    enabled: Boolean(user?.organizationId && chargeId),
  });

  const roleBreadcrumbLabel = roleDetailQuery.data
    ? resolveSystemRoleLabel(roleDetailQuery.data.name, t)
    : null;
  const chargeBreadcrumbLabel = chargeDetailQuery.data?.identifier ?? null;

  const items = [
    {
      title: <Link to="/">Home</Link>,
    },
    ...segments.map((seg, idx) => {
      const rawHref = '/' + segments.slice(0, idx + 1).join('/');
      const href = resolveBreadcrumbHref(rawHref);
      const isLast = idx === segments.length - 1;
      const assetSummary = assetSummaryQuery.data;
      const siteDetail = siteDetailQuery.data;
      const zoneDetail = zoneDetailQuery.data;
      const ticketDetail = ticketDetailQuery.data;

      let title: string = toBreadcrumbTitle(seg);
      if (isAssetDetailPage && isLast && assetSummary) {
        title = assetSummary.assetName;
      } else if (isZoneDetailPage && idx === 1 && zoneDetail) {
        title = zoneDetail.site.name;
      } else if (isZoneDetailPage && isLast && zoneDetail) {
        title = zoneDetail.zone.name;
      } else if (isSiteDetailPage && isLast && siteDetail) {
        title = siteDetail.site.name;
      } else if (isRoleDetailPage && isLast && roleBreadcrumbLabel) {
        title = roleBreadcrumbLabel;
      } else if (isTicketDetailPage && isLast && ticketDetail) {
        title = ticketDetail.code;
      } else if (isChargeDetailPage && isLast && chargeBreadcrumbLabel) {
        title = chargeBreadcrumbLabel;
      }

      if (isLast || href == null) {
        return { title };
      }
      return { title: <Link to={href}>{title}</Link> };
    }),
  ];

  return <AntdBreadcrumb items={items} />;
}
